import { formatMembershipDiscountSummary, MembershipDiscountDetail } from './membership-discount';
import moment from 'moment';

export interface PaymentDiscountRow {
  label: string;
  value: string;
}

const EXCLUDE_CHANNEL_LABEL: Record<string, string> = {
  app: '앱',
  naver: '네이버 스마트스토어',
  kakao: '카카오톡 예약하기',
  benepia: '베네피아',
  ezwel: '이지웰',
};

const MARKET_SECTION_LABEL: Record<string, string> = {
  'last-chance': '라스트찬스',
  'hot-deal': '핫딜',
  'early-bird': '얼리버드',
};

function formatDateTime(value?: Date | string) {
  if (!value) {
    return '';
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY.MM.DD HH:mm') : '';
}

function formatWon(amount?: number) {
  if (amount == null) {
    return '';
  }
  return `${amount.toLocaleString()}원`;
}

function formatPriceChange(originFee?: number, saleFee?: number) {
  if (originFee != null && saleFee != null && originFee !== saleFee) {
    return `${formatWon(originFee)} → ${formatWon(saleFee)}`;
  }
  if (saleFee != null) {
    return formatWon(saleFee);
  }
  if (originFee != null) {
    return formatWon(originFee);
  }
  return '';
}

export function getPaymentDiscountRows(params: {
  product?: {
    applyPeriodDiscount?: boolean;
    discountInfo?: { discountStartDate?: Date | string; discountEndDate?: Date | string; discountedFee?: number };
    periodDiscountExcludeChannels?: string[];
    originFee?: number;
    fee?: number;
    applyGroupDiscount?: boolean;
    isLastChance?: boolean;
    marketSection?: string;
  };
  payment?: {
    couponDiscountAmount?: number;
    membershipDiscountAmount?: number;
    membershipDiscountDetail?: MembershipDiscountDetail | null;
    sellerBurdenDiscountAmount?: number;
    usedCoupons?: { couponInfo?: { name?: string } }[];
    discountSnapshot?: {
      items?: {
        type: 'coupon' | 'membership' | 'period' | 'market' | 'group' | 'seller_burden';
        applied: boolean;
        amount?: number;
        percent?: number;
        label?: string;
        originFee?: number;
        saleFee?: number;
        startDate?: Date | string;
        endDate?: Date | string;
        excludeChannels?: string[];
        excludedOnThisChannel?: boolean;
        couponNames?: string[];
        membership?: MembershipDiscountDetail | null;
        marketSection?: string;
        note?: string;
      }[];
    };
  };
  purchases?: { name?: string | string[]; fee?: number; count?: number }[];
}): PaymentDiscountRow[] {
  const snapshotItems = params.payment?.discountSnapshot?.items;
  if (snapshotItems && snapshotItems.length > 0) {
    return snapshotItemsToRows(snapshotItems);
  }
  return legacyPaymentDiscountRows(params);
}

function snapshotItemsToRows(
  items: NonNullable<NonNullable<Parameters<typeof getPaymentDiscountRows>[0]['payment']>['discountSnapshot']>['items'],
): PaymentDiscountRow[] {
  const rows: PaymentDiscountRow[] = [];
  (items || []).forEach((item) => {
    if (item.type === 'coupon') {
      rows.push({
        label: '쿠폰 할인',
        value: `${formatWon(item.amount)}${item.couponNames?.length ? ` (${item.couponNames.join(', ')})` : ''}`,
      });
      return;
    }
    if (item.type === 'membership') {
      const summary = formatMembershipDiscountSummary(item.membership);
      rows.push({
        label: '멤버십 할인',
        value: `${formatWon(item.amount)}${summary ? ` (${summary})` : ''}`,
      });
      return;
    }
    if (item.type === 'period') {
      const range = [formatDateTime(item.startDate), formatDateTime(item.endDate)].filter(Boolean).join(' ~ ');
      const priceText = formatPriceChange(item.originFee, item.saleFee) || (item.applied ? '적용' : '미적용');
      const status = item.applied ? '' : item.note ? ` (${item.note})` : ' (미적용)';
      rows.push({
        label: '상품 기간한정 할인',
        value: [priceText + status, range].filter(Boolean).join(' · '),
      });
      const excluded = (item.excludeChannels || [])
        .map((channel) => EXCLUDE_CHANNEL_LABEL[channel] || channel)
        .filter(Boolean);
      if (excluded.length > 0) {
        rows.push({
          label: '기간한정 할인 제외 채널',
          value: excluded.join(', '),
        });
      }
      return;
    }
    if (item.type === 'market') {
      const label = item.label || MARKET_SECTION_LABEL[item.marketSection || ''] || '특가';
      rows.push({
        label: `${label} 할인`,
        value: formatPriceChange(item.originFee, item.saleFee) || '적용',
      });
      return;
    }
    if (item.type === 'group') {
      rows.push({
        label: '그룹 할인',
        value: item.amount ? formatWon(item.amount) : '적용',
      });
      return;
    }
    if (item.type === 'seller_burden') {
      rows.push({
        label: '판매자부담 할인',
        value: formatWon(item.amount),
      });
    }
  });
  return rows;
}

function legacyPaymentDiscountRows(params: Parameters<typeof getPaymentDiscountRows>[0]): PaymentDiscountRow[] {
  const rows: PaymentDiscountRow[] = [];
  const product = params.product;
  const payment = params.payment;
  const purchases = params.purchases || [];

  const couponAmount = payment?.couponDiscountAmount || 0;
  if (couponAmount > 0) {
    const couponNames = (payment?.usedCoupons || [])
      .map((coupon) => coupon.couponInfo?.name)
      .filter((name): name is string => !!name);
    rows.push({
      label: '쿠폰 할인',
      value: `${formatWon(couponAmount)}${couponNames.length ? ` (${couponNames.join(', ')})` : ''}`,
    });
  }

  const membershipAmount = payment?.membershipDiscountAmount || 0;
  if (membershipAmount > 0) {
    const summary = formatMembershipDiscountSummary(payment?.membershipDiscountDetail);
    rows.push({
      label: '멤버십 할인',
      value: `${formatWon(membershipAmount)}${summary ? ` (${summary})` : ''}`,
    });
  }

  const discountInfo = product?.discountInfo;
  if (product?.applyPeriodDiscount && discountInfo) {
    const origin = product.originFee;
    const discounted = discountInfo.discountedFee ?? product.fee;
    const range = [formatDateTime(discountInfo.discountStartDate), formatDateTime(discountInfo.discountEndDate)]
      .filter(Boolean)
      .join(' ~ ');
    const priceText =
      origin != null && discounted != null && origin !== discounted
        ? `${formatWon(origin)} → ${formatWon(discounted)}`
        : discounted != null
          ? formatWon(discounted)
          : '적용';
    rows.push({
      label: '상품 기간한정 할인',
      value: [priceText, range].filter(Boolean).join(' · '),
    });
    const excluded = (product.periodDiscountExcludeChannels || [])
      .map((channel) => EXCLUDE_CHANNEL_LABEL[channel] || channel)
      .filter(Boolean);
    if (excluded.length > 0) {
      rows.push({
        label: '기간한정 할인 제외 채널',
        value: excluded.join(', '),
      });
    }
  }

  const marketSection = product?.marketSection;
  if (
    product?.isLastChance ||
    marketSection === 'last-chance' ||
    marketSection === 'hot-deal' ||
    marketSection === 'early-bird'
  ) {
    const label = MARKET_SECTION_LABEL[marketSection || ''] || '특가';
    rows.push({
      label: `${label} 할인`,
      value: formatPriceChange(product?.originFee, product?.fee) || '적용',
    });
  }

  const groupDiscountLine = purchases.find((item) => item.name === 'discount');
  if (product?.applyGroupDiscount || groupDiscountLine) {
    const amount = Math.abs(groupDiscountLine?.fee || 0);
    rows.push({
      label: '그룹 할인',
      value: amount > 0 ? formatWon(amount) : '적용',
    });
  }

  const sellerBurden = payment?.sellerBurdenDiscountAmount || 0;
  if (sellerBurden > 0) {
    rows.push({
      label: '판매자부담 할인',
      value: formatWon(sellerBurden),
    });
  }

  return rows;
}
